'use client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiAttribute } from '@context/openapi/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@components/components/ui/tooltip';
import { cn } from '@components/lib/utils';

export function useTruncation(
  ref: React.RefObject<HTMLElement | null>,
  deps: React.DependencyList = []
) {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const textElement =
      el.querySelector('span span') || el.querySelector('span') || el;

    if (!textElement) return;

    const check = () => {
      const truncated = textElement.scrollWidth > textElement.clientWidth;
      setIsTruncated(truncated);
    };

    check();
    window.addEventListener('resize', check);

    return () => window.removeEventListener('resize', check);
  }, [ref, ...deps]);

  return isTruncated;
}

interface EnumInputProps {
  field: ApiAttribute;
  value: string | number;
  onChange?: (field: ApiAttribute, value: string) => void;
  ref?: React.Ref<HTMLSelectElement>;
  setIsFocused?: (visible: boolean) => void;
  isFocused?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const Dropdown: React.FC<EnumInputProps> = ({
  field,
  value,
  onChange,
  setIsFocused,
  placeholder,
  disabled = false,
}) => {
  const items = useMemo(
    () =>
      field.child?.map((c) => {
        return { key: c.key, value: c.name };
      }) ?? [],
    [field.child]
  );

  const triggerRef = useRef<HTMLButtonElement>(null); // ⭐ MODIFIED

  const handleValueChange = useCallback(
    (selectedValue: string) => {
      if (!onChange) return;
      onChange(field, selectedValue);
    },
    [field, onChange]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (setIsFocused) setIsFocused(open);
    },
    [setIsFocused]
  );

  const placeholderText = placeholder || 'Select an option';

  const selectedItem = useMemo(() => {
    if (!value) return null;
    return items.find((item) => item.value === String(value));
  }, [value, items]);

  const isTruncated = useTruncation(triggerRef, [selectedItem, value]);
  const selectedText = selectedItem?.value ?? '';

  const isEnumFilterValue =
    field.type === 'ENUM' && placeholder === 'Select a Value';

  const selectTrigger = (
    <SelectTrigger
      ref={triggerRef}
      className={cn(
        'h-6 pl-2 pr-6 w-full border-l border-form-border cursor-pointer bg-white',
        field.type === 'FILTER' &&
          !isEnumFilterValue &&
          'appearance-auto !bg-gray-100',
        placeholderText === 'Select' && 'pr-2',
        value === '' ? 'text-xs text-neutral-400 py-1' : 'text-sm',
        'rounded-none border-0 border-l shadow-none',
        'min-w-0',
        '[&>span>span]:truncate [&>span>span]:min-w-0 [&>span>span]:shrink',
        'focus-visible:!outline-shade-aqua-300'
      )}
      data-pendo-mask
    >
      <SelectValue
        placeholder={placeholderText}
        className="truncate min-w-0 block"
      />
    </SelectTrigger>
  );

  return (
    <div className="w-full flex items-center min-w-0 overflow-visible">
      <Select
        value={value ? String(value) : ''}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled}
      >
        {isTruncated && selectedText ? (
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>{selectTrigger}</TooltipTrigger>
              <TooltipContent side="right" className="max-w-[400px]">
                <p>{selectedText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          selectTrigger
        )}

        <SelectContent className="max-w-none">
          {items.map((item) => (
            <SelectItem key={item.key} value={item.value}>
              {item.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default Dropdown;
